/**
 * Utility functions for handling S3 URLs and brand logos
 */

/**
 * Get the S3 base URL from environment or default
 */
const getS3BaseUrl = (): string => {
  // In production, this would come from environment variables
  // For now, we'll use the default podscope bucket
  return 'https://podscope.s3.amazonaws.com';
};

/**
 * Generate a full S3 URL from an S3 key
 * @param s3Key - The S3 key (e.g., "brands/123/image.png")
 * @returns Full S3 URL
 */
export const getS3Url = (s3Key: string): string => {
  if (!s3Key) return '';
  
  // Remove leading slash if present
  const cleanKey = s3Key.startsWith('/') ? s3Key.slice(1) : s3Key;
  
  return `${getS3BaseUrl()}/${cleanKey}`;
};

/**
 * Get brand logo URL, preferring S3 key over legacy logoUrl
 * @param brand - Brand object with s3Key and/or logoUrl
 * @returns Logo URL or empty string
 */
export const getBrandLogoUrl = (brand: { s3Key?: string; logoUrl?: string }): string => {
  // Prefer s3Key over logoUrl
  if (brand.s3Key) {
    return getS3Url(brand.s3Key);
  }
  
  // Fallback to legacy logoUrl
  if (brand.logoUrl) {
    return brand.logoUrl;
  }
  
  return '';
};

/**
 * Get brand logo URL with fallback to placeholder
 * @param brand - Brand object with s3Key and/or logoUrl
 * @param placeholder - Placeholder image URL (default: '/placeholder-logo.png')
 * @returns Logo URL or placeholder
 */
export const getBrandLogoUrlWithFallback = (
  brand: { s3Key?: string; logoUrl?: string }, 
  placeholder: string = '/placeholder-logo.png'
): string => {
  const logoUrl = getBrandLogoUrl(brand);
  return logoUrl || placeholder;
};

/**
 * Check if a brand has a logo (either S3 or legacy)
 * @param brand - Brand object with s3Key and/or logoUrl
 * @returns true if brand has a logo
 */
export const hasBrandLogo = (brand: { s3Key?: string; logoUrl?: string }): boolean => {
  return !!(brand.s3Key || brand.logoUrl);
};

/**
 * Get brand logo props for Material-UI Image components
 * @param brand - Brand object with s3Key and/or logoUrl
 * @param placeholder - Placeholder image URL
 * @returns Object with src and onError handler
 */
export const getBrandLogoProps = (
  brand: { s3Key?: string; logoUrl?: string },
  placeholder: string = '/placeholder-logo.png'
) => {
  const logoUrl = getBrandLogoUrl(brand);
  
  return {
    src: logoUrl || placeholder,
    onError: (e: React.SyntheticEvent<HTMLImageElement>) => {
      const target = e.target as HTMLImageElement;
      if (target.src !== placeholder) {
        target.src = placeholder;
      }
    }
  };
};

/** Fill missing logo fields from other mentions of the same brand in the episode. */
export function resolveBrandLogoFromMentions(
  mention: { brandId?: string; brandName?: string; s3Key?: string; logoUrl?: string },
  allMentions: Array<{ brandId?: string; brandName?: string; s3Key?: string; logoUrl?: string }>,
): { s3Key?: string; logoUrl?: string } {
  if (mention.s3Key || mention.logoUrl) {
    return { s3Key: mention.s3Key, logoUrl: mention.logoUrl };
  }

  for (const candidate of allMentions) {
    if (!candidate.s3Key && !candidate.logoUrl) continue;
    if (mention.brandId && candidate.brandId === mention.brandId) {
      return { s3Key: candidate.s3Key, logoUrl: candidate.logoUrl };
    }
  }

  for (const candidate of allMentions) {
    if (!candidate.s3Key && !candidate.logoUrl) continue;
    if (mention.brandName && candidate.brandName === mention.brandName) {
      return { s3Key: candidate.s3Key, logoUrl: candidate.logoUrl };
    }
  }

  return {};
};

